import { useState } from 'react';
import { Box, Typography, IconButton, Button, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import ArticleIcon from '@mui/icons-material/Article';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import YouTubeEmbed from './YouTubeEmbed';
import Highlight from './Highlight';

const PP = "'Poppins', sans-serif";
const API_BASE = process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '') || '';

function kindIcon(kind) {
    switch (kind) {
        case 'video': return <PlayArrowIcon sx={{ fontSize: 18, color: '#EF4444' }} />;
        case 'image': return <ImageIcon sx={{ fontSize: 18, color: '#6366F1' }} />;
        case 'pdf':   return <DescriptionIcon sx={{ fontSize: 18, color: '#1A8CFF' }} />;
        case 'link':  return <LinkIcon sx={{ fontSize: 18, color: '#6366F1' }} />;
        case 'text':
        default:      return <ArticleIcon sx={{ fontSize: 18, color: '#94A3B8' }} />;
    }
}

function formatFileSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
}

function resolveImageUrl(url) {
    if (!url) return url;
    return url.startsWith('/') ? `${API_BASE}${url}` : url;
}

export default function LearningCard({ item, canManage, onEdit, onDelete, query }) {
    const [expanded, setExpanded] = useState(false);

    const handleQuickAction = (e) => {
        e.stopPropagation();
        if (item.kind === 'link' && item.externalUrl) {
            window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
        } else if (item.kind === 'pdf' && item.fileUrl) {
            window.open(`${API_BASE}${item.fileUrl}`, '_blank', 'noopener,noreferrer');
        }
    };

    const handleEdit = (e) => {
        e.stopPropagation();
        if (onEdit) onEdit(item);
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        if (onDelete) onDelete(item);
    };

    const hasQuickAction = (item.kind === 'link' && item.externalUrl) || (item.kind === 'pdf' && item.fileUrl);
    const canExpand = item.kind === 'video' || item.kind === 'image' || (item.description && item.description.trim());

    return (
        <Box sx={{ borderBottom: '1px solid #F1F5F9', '&:hover': { backgroundColor: '#F8FAFC' } }}>
            <Box
                onClick={() => canExpand && setExpanded((p) => !p)}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, px: 2, cursor: canExpand ? 'pointer' : 'default' }}
            >
                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                    {kindIcon(item.kind)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, fontFamily: PP, color: '#0F172A' }}>
                        <Highlight text={item.title} query={query} />
                    </Typography>
                    {item.description && !expanded && (
                        <Typography
                            sx={{
                                fontSize: '0.72rem', color: '#64748B', fontFamily: PP,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
                            }}
                        >
                            <Highlight text={item.description} query={query} />
                        </Typography>
                    )}
                    {item.fileName && item.fileSize > 0 && (
                        <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8', fontFamily: PP }}>
                            {item.fileName} · {formatFileSize(item.fileSize)}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {hasQuickAction && (
                        <Button
                            size="small"
                            endIcon={item.kind === 'link' ? <OpenInNewIcon sx={{ fontSize: 14 }} /> : <DownloadIcon sx={{ fontSize: 14 }} />}
                            onClick={handleQuickAction}
                            sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.75rem', color: item.kind === 'link' ? '#6366F1' : '#1A8CFF' }}
                        >
                            {item.kind === 'link' ? 'Open' : 'Download'}
                        </Button>
                    )}
                    {canManage && onEdit && (
                        <IconButton size="small" onClick={handleEdit} title="Edit">
                            <EditIcon sx={{ fontSize: 16, color: '#64748B' }} />
                        </IconButton>
                    )}
                    {canManage && onDelete && (
                        <IconButton size="small" onClick={handleDelete} title="Delete">
                            <DeleteIcon sx={{ fontSize: 16, color: '#EF4444' }} />
                        </IconButton>
                    )}
                    {canExpand && (
                        <ExpandMoreIcon
                            sx={{
                                fontSize: 20, color: '#94A3B8',
                                transition: 'transform 0.2s',
                                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                        />
                    )}
                </Box>
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box sx={{ px: 4, pb: 2.5, pt: 0.5 }}>
                    {item.description && (
                        <Typography
                            sx={{
                                fontFamily: PP, fontSize: '0.82rem', color: '#374151', lineHeight: 1.7,
                                whiteSpace: 'pre-wrap',
                                mb: item.videoUrls.length || item.mediaUrls.length ? 1.5 : 0,
                            }}
                        >
                            <Highlight text={item.description} query={query} />
                        </Typography>
                    )}
                    {item.videoUrls.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
                            {item.videoUrls.map((url, i) => (
                                <YouTubeEmbed key={i} url={url} />
                            ))}
                        </Box>
                    )}
                    {item.mediaUrls.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {item.mediaUrls.map((url, i) => {
                                const src = resolveImageUrl(url);
                                return (
                                    <a key={i} href={src} target="_blank" rel="noreferrer">
                                        <img
                                            src={src} alt=""
                                            style={{ maxWidth: 200, maxHeight: 150, borderRadius: 8, objectFit: 'cover', border: '1px solid #E2E8F0' }}
                                        />
                                    </a>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
}
